import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboard } from '../contexts/DashboardContext.jsx';
import { api } from '../utils/api.js';
import { API_ENDPOINTS } from '../config/api.js';
import Header from '../components/Header.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import ErrorMessage from '../components/ErrorMessage.jsx';
import { getFriendlyDate, formatDateTime, getDateRange } from '../utils/dateUtils.js';
import { DIMENSIONS } from '../constants/gameConstants.js';
import { colors, typography, spacing, radius, components } from '../../styles/theme.js';

const StatsPage = () => {
  const { user } = useAuth();
  const { dashboardData, fetchDashboard } = useDashboard();
  const [activeTab, setActiveTab] = useState('nback'); // 'nback' or 'speed'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [combos, setCombos] = useState([]);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);

  useEffect(() => {
    loadCombos();
    fetchDashboard(); // Load speed training stats
  }, [fetchDashboard]);

  useEffect(() => {
    if (selectedCombo) {
      loadComboStats();
      loadComboHistory();
    }
  }, [selectedCombo, timeFilter]);

  async function loadCombos() {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(API_ENDPOINTS.GET_COMBOS);
      setCombos(response.combos || []);
      setGlobalStats({
        totalRounds: response.totalRounds || 0,
        totalSessions: response.totalSessions || 0,
        totalDays: response.totalDaysTrained || 0,
        totalCombos: response.count || 0
      });
      
      // Select first combo by default
      if (response.combos && response.combos.length > 0) {
        setSelectedCombo(response.combos[0].comboCode);
      }
    } catch (err) {
      console.error('Failed to load combos:', err);
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }

  async function loadComboStats() {
    if (!selectedCombo) return;
    
    try {
      const dateFilter = getDateRange(timeFilter);
      const response = await api.get(
        API_ENDPOINTS.GET_COMBO_STATS(selectedCombo),
        dateFilter
      );
      setStats(response.stats);
    } catch (err) {
      console.error('Failed to load combo stats:', err);
      setStats(null);
    }
  }

  async function loadComboHistory() {
    if (!selectedCombo) return;
    
    try {
      const dateFilter = getDateRange(timeFilter);
      const response = await api.get(
        API_ENDPOINTS.GET_COMBO_HISTORY(selectedCombo),
        { ...dateFilter, limit: 20 }
      );
      setHistory(response.rounds || []);
    } catch (err) {
      console.error('Failed to load combo history:', err);
      setHistory([]);
    }
  }

  if (loading) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LoadingSpinner message="Loading statistics..." />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          padding: '2rem'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <ErrorMessage error={error} onRetry={loadCombos} />
          </div>
        </div>
      </>
    );
  }

  if (combos.length === 0) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: colors.bgPrimary,
          padding: spacing[8],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: typography.fontSans
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{ fontSize: '4rem', marginBottom: spacing[4] }}>📊</div>
            <h2 style={{ 
              color: colors.textPrimary, 
              marginBottom: spacing[2],
              fontSize: typography['2xl'],
              fontWeight: typography.bold,
              letterSpacing: '-0.01em'
            }}>
              No Training Data Yet
            </h2>
            <p style={{ 
              color: colors.textTertiary,
              fontSize: typography.base,
              lineHeight: typography.relaxed
            }}>
              Complete your first training session to see your statistics here!
            </p>
          </div>
        </div>
      </>
    );
  }

  const codeToMapping = {
    'P': 'position', 'A': 'audio', 'V': 'voice', 'M': 'note',
    'C': 'color', 'S': 'shape', 'N': 'number', 'F': 'face',
    'R': 'arrow', 'O': 'movement', 'T': 'pattern', 'B': 'border',
    'L': 'orbitalPosition', 'Z': 'circleSize', 'X': 'circleRotation', 'I': 'circleBrightness',
    'W': 'categoryText', 'E': 'categoryAudio', 'Y': 'nameText', 'U': 'nameAudio', 'Q': 'math', 'H': 'rhythm', 'D': 'mandarinTone', 'G': 'volume'
  };

  const getDimensionLabel = (dimensions) => {
    return dimensions.map(dim => {
      const label = DIMENSIONS[dim]?.name || dim;
      // Capitalize first letter of each word
      return label.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }).join(' + ');
  };

  return (
    <>
      <Header />
      <div style={{
        minHeight: 'calc(100vh - 64px)',
        background: colors.bgPrimary,
        padding: spacing[8],
        fontFamily: typography.fontSans
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Page Header - Improved hierarchy */}
          <div style={{ marginBottom: spacing[8] }}>
            <h1 style={{
              fontSize: typography['4xl'],
              fontWeight: typography.extrabold,
              marginBottom: spacing[2],
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: typography.tight
            }}>
              Training Statistics
            </h1>
            
            <p style={{
              color: colors.textTertiary,
              fontSize: typography.base,
              margin: 0
            }}>
              Track your progress across all training modalities
            </p>
          </div>

          {/* Tabs for N-Back and Speed Training */}
          <div style={{
            display: 'flex',
            gap: spacing[2],
            marginBottom: spacing[6],
            borderBottom: `1px solid ${colors.border}`
          }}>
            {['nback', 'speed'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: `${spacing[4]} ${spacing[6]}`,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab ? `3px solid ${colors.primary}` : '3px solid transparent',
                  color: activeTab === tab ? colors.primary : colors.textSecondary,
                  fontSize: typography.base,
                  fontWeight: typography.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: typography.fontSans,
                  marginBottom: '-1px'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab) {
                    e.currentTarget.style.color = colors.textPrimary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab) {
                    e.currentTarget.style.color = colors.textSecondary;
                  }
                }}
              >
                {tab === 'nback' ? 'N-Back Training' : 'Speed Training'}
              </button>
            ))}
          </div>

          {/* N-Back Stats (existing content) */}
          {activeTab === 'nback' && (
            <>
              {/* Global Stats - Cleaner cards */}
              {globalStats && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: spacing[4],
                  marginBottom: spacing[8]
                }}>
                  {[
                    { label: 'Total Rounds', value: globalStats.totalRounds, color: colors.primary },
                    { label: 'Total Sessions', value: globalStats.totalSessions, color: colors.secondary },
                    { label: 'Days Trained', value: globalStats.totalDays, color: colors.accent },
                    { label: 'Combos Tried', value: globalStats.totalCombos, color: colors.warning }
                  ].map(stat => (
                    <div key={stat.label} style={{
                      ...components.card,
                      padding: spacing[5],
                      transition: 'transform 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    >
                      <div style={{ 
                        fontSize: typography['3xl'], 
                        fontWeight: typography.bold, 
                        color: stat.color,
                        marginBottom: spacing[1],
                        lineHeight: typography.tight
                      }}>
                        {stat.value}
                      </div>
                      <div style={{ 
                        fontSize: typography.sm, 
                        color: colors.textTertiary,
                        fontWeight: typography.medium
                      }}>
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}

          {/* Combo Selector - Card-style dropdown */}
          <div style={{
            marginBottom: spacing[4]
          }}>
            <label style={{
              display: 'block',
              color: colors.textTertiary,
              fontSize: typography.xs,
              fontWeight: typography.semibold,
              marginBottom: spacing[2],
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Viewing Stats For
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedCombo || ''}
                onChange={(e) => setSelectedCombo(e.target.value)}
                style={{
                  width: '100%',
                  padding: spacing[4],
                  borderRadius: radius.lg,
                  border: 'none',
                  background: 'rgba(30, 41, 59, 0.6)',
                  color: colors.textPrimary,
                  fontSize: typography.base,
                  cursor: 'pointer',
                  fontFamily: typography.fontSans,
                  fontWeight: typography.semibold,
                  transition: 'all 0.15s ease',
                  appearance: 'none',
                  paddingRight: '3rem',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                {combos.map(combo => (
                  <option key={combo.comboCode} value={combo.comboCode}>
                    {getDimensionLabel(combo.comboCode.split('').map(c => codeToMapping[c] || c))} - Level {combo.currentLevel}
                  </option>
                ))}
              </select>
              <div style={{
                position: 'absolute',
                right: spacing[4],
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: colors.textTertiary
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>

          {/* Time Filter - Button group */}
          <div style={{
            display: 'flex', 
            gap: spacing[2],
            flexWrap: 'wrap',
            marginBottom: spacing[8]
          }}>
            {['all', 'week', 'month', 'year'].map(filter => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                style={{
                  padding: `${spacing[3]} ${spacing[5]}`,
                  borderRadius: radius.lg,
                  border: `1px solid ${timeFilter === filter ? colors.primary : colors.borderLight}`,
                  background: timeFilter === filter ? 'rgba(96, 165, 250, 0.1)' : 'transparent',
                  color: timeFilter === filter ? colors.primary : colors.textSecondary,
                  fontSize: typography.sm,
                  fontWeight: typography.semibold,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textTransform: 'capitalize',
                  fontFamily: typography.fontSans,
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  if (timeFilter !== filter) {
                    e.currentTarget.style.background = 'rgba(96, 165, 250, 0.05)';
                    e.currentTarget.style.borderColor = colors.border;
                  }
                }}
                onMouseLeave={(e) => {
                  if (timeFilter !== filter) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = colors.borderLight;
                  }
                }}
              >
                {filter === 'all' ? 'All Time' : `Last ${filter.charAt(0).toUpperCase() + filter.slice(1)}`}
              </button>
            ))}
          </div>

          {/* Combo Stats - Better spacing */}
          {stats && (
            <div style={{
              ...components.card,
              padding: spacing[6],
              marginBottom: spacing[6]
            }}>
              <h2 style={{
                fontSize: typography.xl,
                fontWeight: typography.bold,
                color: colors.textPrimary,
                marginBottom: spacing[6],
                letterSpacing: '-0.01em'
              }}>
                {getDimensionLabel(stats.dimensions)} Stats
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: spacing[5],
                marginBottom: spacing[6]
              }}>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Rounds
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: colors.primary,
                    lineHeight: typography.tight
                  }}>
                    {stats.totalRounds}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Sessions
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: colors.secondary,
                    lineHeight: typography.tight
                  }}>
                    {stats.totalSessions}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Days
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: colors.accent,
                    lineHeight: typography.tight
                  }}>
                    {stats.totalDays}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Recommended
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: colors.warning,
                    lineHeight: typography.tight
                  }}>
                    {stats.currentLevel}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Highest
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: '#f97316',
                    lineHeight: typography.tight
                  }}>
                    {stats.highestLevel}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    fontWeight: typography.medium,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Accuracy
                  </div>
                  <div style={{ 
                    fontSize: typography['2xl'], 
                    fontWeight: typography.bold, 
                    color: '#ec4899',
                    lineHeight: typography.tight
                  }}>
                    {stats.avgAccuracy}%
                  </div>
                </div>
              </div>

              {/* Per-dimension breakdown */}
              {stats.dimensionStats && (
                <div>
                  <h3 style={{
                    fontSize: typography.base,
                    fontWeight: typography.semibold,
                    color: colors.textSecondary,
                    marginBottom: spacing[4],
                    letterSpacing: '-0.01em'
                  }}>
                    Per-Dimension Breakdown
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
                    {Object.entries(stats.dimensionStats).map(([dim, dimStats]) => (
                      <div key={dim} style={{
                        background: colors.bgInput,
                        borderRadius: radius.md,
                        padding: spacing[4],
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ 
                            color: colors.textPrimary, 
                            fontWeight: typography.semibold,
                            marginBottom: spacing[1]
                          }}>
                            {DIMENSIONS[dim]?.name || dim}
                          </div>
                          <div style={{ 
                            fontSize: typography.sm, 
                            color: colors.textTertiary 
                          }}>
                            {dimStats.totalCorrect} / {dimStats.totalTrials} correct
                          </div>
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.primary 
                        }}>
                          {dimStats.percentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent History - Improved */}
          {history.length > 0 && (
            <div style={{
              ...components.card,
              padding: spacing[6]
            }}>
              <h2 style={{
                fontSize: typography.xl,
                fontWeight: typography.bold,
                color: colors.textPrimary,
                marginBottom: spacing[5],
                letterSpacing: '-0.01em'
              }}>
                Recent History ({history.length} rounds)
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
                {history.map((round, idx) => (
                  <div key={idx} style={{
                    background: colors.bgInput,
                    borderRadius: radius.md,
                    padding: spacing[4],
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: spacing[4]
                  }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ 
                        color: colors.textPrimary, 
                        fontWeight: typography.semibold,
                        marginBottom: spacing[1]
                      }}>
                        {getFriendlyDate(round.timestamp)}
                      </div>
                      <div style={{ 
                        fontSize: typography.sm, 
                        color: colors.textTertiary 
                      }}>
                        {formatDateTime(round.timestamp)}
                      </div>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      gap: spacing[6], 
                      alignItems: 'center' 
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary,
                          marginBottom: spacing[1],
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: typography.medium
                        }}>
                          Level
                        </div>
                        <div style={{ 
                          fontSize: typography.xl, 
                          fontWeight: typography.bold, 
                          color: colors.primary 
                        }}>
                          {round.nValue}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary,
                          marginBottom: spacing[1],
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: typography.medium
                        }}>
                          Accuracy
                        </div>
                        <div style={{ 
                          fontSize: typography.xl, 
                          fontWeight: typography.bold, 
                          color: colors.accent 
                        }}>
                          {round.overallAccuracy}%
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary,
                          marginBottom: spacing[1],
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontWeight: typography.medium
                        }}>
                          Result
                        </div>
                        <div style={{ 
                          fontSize: typography.sm, 
                          fontWeight: typography.semibold,
                          color: round.recommendation === 'level_up' ? colors.accent : 
                                 round.recommendation === 'level_down' ? colors.error : colors.textTertiary
                        }}>
                          {round.recommendation === 'level_up' ? '↑ Level Up' :
                           round.recommendation === 'level_down' ? '↓ Level Down' : '→ Same'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
            </>
          )}

          {/* Speed Training Stats */}
          {activeTab === 'speed' && (
            <>
              {dashboardData?.speedTraining ? (
                <div>
                  {/* Inspection Time Stats */}
                  <div style={{
                    ...components.card,
                    padding: spacing[6],
                    marginBottom: spacing[6]
                  }}>
                    <h2 style={{
                      fontSize: typography.xl,
                      fontWeight: typography.bold,
                      color: colors.primary,
                      marginBottom: spacing[5],
                      letterSpacing: '-0.01em'
                    }}>
                      Inspection Time
                    </h2>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: spacing[5]
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Best Duration
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.primary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.inspection?.globalStats?.bestDuration ? `${dashboardData.speedTraining.inspection.globalStats.bestDuration}ms` : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Total Trials
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.secondary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.inspection?.globalStats?.totalTrials || 0}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Average Accuracy
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.accent,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.inspection?.globalStats?.avgAccuracy ? `${Math.round(dashboardData.speedTraining.inspection.globalStats.avgAccuracy)}%` : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Last Played
                        </div>
                        <div style={{ 
                          fontSize: typography.sm, 
                          fontWeight: typography.semibold, 
                          color: colors.textSecondary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.inspection?.globalStats?.lastPlayed ? getFriendlyDate(dashboardData.speedTraining.inspection.globalStats.lastPlayed) : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reaction Time Stats */}
                  <div style={{
                    ...components.card,
                    padding: spacing[6]
                  }}>
                    <h2 style={{
                      fontSize: typography.xl,
                      fontWeight: typography.bold,
                      color: colors.warning,
                      marginBottom: spacing[5],
                      letterSpacing: '-0.01em'
                    }}>
                      Reaction Time
                    </h2>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: spacing[5]
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          2-Choice Best
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.primary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.reaction?.modes?.['2']?.bestRT ? `${Math.round(dashboardData.speedTraining.reaction.modes['2'].bestRT)}ms` : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          4-Choice Best
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.secondary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.reaction?.modes?.['4']?.bestRT ? `${Math.round(dashboardData.speedTraining.reaction.modes['4'].bestRT)}ms` : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          8-Choice Best
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.accent,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.reaction?.modes?.['8']?.bestRT ? `${Math.round(dashboardData.speedTraining.reaction.modes['8'].bestRT)}ms` : '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Total Trials
                        </div>
                        <div style={{ 
                          fontSize: typography['2xl'], 
                          fontWeight: typography.bold, 
                          color: colors.warning,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.reaction?.globalStats?.totalTrials || 0}
                        </div>
                      </div>
                      <div>
                        <div style={{ 
                          fontSize: typography.xs, 
                          color: colors.textTertiary, 
                          marginBottom: spacing[1],
                          fontWeight: typography.medium,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Last Played
                        </div>
                        <div style={{ 
                          fontSize: typography.sm, 
                          fontWeight: typography.semibold, 
                          color: colors.textSecondary,
                          lineHeight: typography.tight
                        }}>
                          {dashboardData.speedTraining.reaction?.globalStats?.lastPlayed ? getFriendlyDate(dashboardData.speedTraining.reaction.globalStats.lastPlayed) : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  ...components.card,
                  padding: spacing[8],
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: spacing[4] }}>⚡</div>
                  <h2 style={{ 
                    color: colors.textPrimary, 
                    marginBottom: spacing[2],
                    fontSize: typography['2xl'],
                    fontWeight: typography.bold
                  }}>
                    No Speed Training Data Yet
                  </h2>
                  <p style={{ 
                    color: colors.textTertiary,
                    fontSize: typography.base,
                    marginBottom: spacing[4]
                  }}>
                    Complete a speed training session to see your statistics here!
                  </p>
                  <button
                    onClick={() => window.location.href = '/speed-training'}
                    style={{
                      padding: `${spacing[4]} ${spacing[6]}`,
                      background: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.warningLight} 100%)`,
                      border: 'none',
                      borderRadius: radius.lg,
                      color: '#1f2937',
                      fontSize: typography.base,
                      fontWeight: typography.bold,
                      cursor: 'pointer',
                      fontFamily: typography.fontSans
                    }}
                  >
                    Start Speed Training
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StatsPage;

